"use client";

import React from "react";
import { Button } from "../../atoms/button/button";
import { ctaAriaLabel } from "../../services/cta-aria-label";
import { fireItemListSelect } from "../../services/analytics";
import {
  SHOP_ITEM_LIST_ID,
  SHOP_ITEM_LIST_NAME,
  shopLinkName
} from "./lineup-analytics.shared";
import styles from "./package-tile.module.css";
import { withSafeRender } from "../../atoms/safe-render";

/** GH2's Shop button, and AN3's `select_item` beside it. The client-boundary argument. */

export interface PackageShopCtaProps {
  /** The button's wording, already defaulted and reduced to plain text. */
  label: string;
  /** The package's name, already reduced — AN3's link name. */
  packageName: string;
  /** The button's accessible name, as authored. Absent, the visible wording names it. */
  ariaLabel?: string;
  /** The shop destination, already judged safe by the caller. */
  href: string;
  /** AN3 — the column's position, **starting at 0**, reported as both the item id and the index. */
  index: number;
  /** AN3's placement — pushed with the `link_click`, not written on the link.
   *  Defaults to `LINK_POSITION_BODY`, which is what production reports. */
  linkPosition?: string;
}

function PackageShopCtaImpl({
  label,
  packageName,
  ariaLabel,
  href,
  index,
  linkPosition
}: PackageShopCtaProps) {
  const report = () => {
    fireItemListSelect({
      /* The position is the identity. */
      index,
      itemId: index,
      /* The button's label, not the package's name — production passes `ctaProps?.label`, so every
         row of this report reads "Shop". */
      itemName: label,
      itemListId: SHOP_ITEM_LIST_ID,
      itemListName: SHOP_ITEM_LIST_NAME
    });
  };

  return (
    /* GH2 — "the tonal style shown in Figma, not the filled orange style", which is
       `variant="tonal"` with no tone: its on-dark set is the translucent white-on-dark pill GH9
       describes. */
    <Button
      ariaLabel={ctaAriaLabel(ariaLabel)}
      className={styles.cta}
      href={href}
      /* AN3's renamed `link_click`, pushed by the atom. `linkName` and `linkAttributes` exclude
         each other in `ButtonAnalyticsProps`, which is what keeps the attribute route and the push
         route from ever being on at once — see the header. */
      linkName={shopLinkName(label, packageName)}
      linkPosition={linkPosition}
      onClick={report}
      size="small"
      variant="tonal"
    >
      {label}
    </Button>
  );
}
export const PackageShopCta = withSafeRender(PackageShopCtaImpl, "PackageShopCta");

export default PackageShopCta;
